/**
 * O casco do painel da Escola (`{slug}.escola.ao`): o `EstruturaPainel` do kit com o menu
 * `navigation_escola`, a cor da Escola Inteligente (#083657), o cartão da escola com «Trocar»
 * (`EscolaSeletor`), a pesquisa de alunos e as notificações. Embrulha o `MainProvider` (o `/vs`,
 * o acesso a expirar, os pagamentos) e o `MainContext` (as ocorrências das creches).
 *
 * As páginas da Escola abertas dentro do Logigate (`/escola-inteligente`) não passam por
 * aqui: usam o casco do Logigate (ver `UserLayout` em `AuthLayout.tsx`).
 */
import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { usePage } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import { Mailbox } from "lucide-react";
import { BotaoIcone } from "@/Logigate/ui/base";
import { EstruturaPainel, ItemMenuServidor, JanelaNovidades, menuDoServidor, partesDoEndereco, useApresentacaoDoPainel } from "@/Logigate/ui/painel";
import { useTema } from "@/Logigate/ui/tema";
import { EVENTO, subscribe, unsubscribe } from "@/utils/events";
import req from "@/utils/req";
import { NOVIDADES_ESCOLA, TUTORIAL_ESCOLA } from "./apresentacaoEscola";
import AvisoServicosPagos from "@/components/escola/AvisoServicosPagos";
import AvisoRescisao from "@/components/escola/AvisoRescisao";
import { MainProvider } from "@/Escola/@core/components/MainProvider";
import { MainContext, useMain } from "@/Escola/@core/context/MainContext";
import { DialogPesquisarAlunos } from "@/Escola/@core/layouts/components/DialogPesquisarAlunos";
import EscolaSeletor from "@/Escola/estrutura/EscolaSeletor";

interface Props {
    children: ReactNode;
    semMargens?: boolean;
}

export function EstruturaEscola(props: Props) {
    return (
        <MainProvider>
            <MainContext>
                <CascoEscola {...props} />
            </MainContext>
        </MainProvider>
    );
}

function CascoEscola({ children, semMargens }: Props) {
    const { props, url } = usePage<any>();
    // O modo claro/escuro do kit (`TemaProvider` no app.tsx): a chave `vite-ui-theme`; sem escolha, segue o aparelho.
    const { tema, setTema, escuro } = useTema();
    const { setShowDialogOcorrencias } = useMain();
    const [pesquisarAlunos, setPesquisarAlunos] = useState(false);
    const [totalNotificacoes, setTotalNotificacoes] = useState<number | string>(props.total_n || 0);

    // O casco é persistente (não se volta a montar entre páginas): sem isto o sino ficava com o
    // número da primeira página, mesmo depois de a página das notificações as dar por vistas.
    useEffect(() => setTotalNotificacoes(props.total_n || 0), [props.total_n]);

    useEffect(() => {
        const notifica = (e: any) => {
            if (e.detail?.total_n !== undefined) {
                setTotalNotificacoes(e.detail.total_n);
            }
        };
        subscribe(EVENTO.NOTIFICA, notifica);
        return () => unsubscribe(EVENTO.NOTIFICA, notifica);
    }, []);

    const escola = props.escola ?? {};
    const nome: string = escola?.info?.nome || "Escola";
    const user = props.auth?.user;

    // As novidades (a quem já era secretaria antes delas) e depois o tutorial, uma vez cada; voltam a
    // pedido no menu da conta.
    const apresentacao = useApresentacaoDoPainel({
        pendentes: props.apresentacao,
        marcarVisto: (qual) => void req().post(`/apresentacao/${qual}/visto`),
        abrirTutorial: (aoTerminar) =>
            void import("@/Logigate/ui/painel/tutorialDoPainel").then(({ abrirTutorialDoPainel }) => abrirTutorialDoPainel({ nome: user?.nome, textos: TUTORIAL_ESCOLA, aoTerminar })),
    });
    const itens = useMemo(() => menuDoServidor(props.navigation_escola as ItemMenuServidor[]), [props.navigation_escola]);

    const caminho = partesDoEndereco(url)?.caminho ?? "/";
    const urls = (window as any).urls ?? {};

    return (
        <>
            <EstruturaPainel
                chave="escola"
                cor="escola"
                // A marca é o produto, como no Logigate: a escola (logótipo e nome) está no cartão logo abaixo.
                marca={{
                    logotipo: <img src="/escola/imagens/logo.png" alt="" className="h-6 w-auto" />,
                    nome: "Escola Inteligente",
                    detalhe: nome,
                    href: "/",
                }}
                itens={itens}
                // A escola deste painel e, para quem gere várias, «Trocar» (como o seletor de empresas do Logigate).
                antesDoMenu={escola?.info?.id ? <EscolaSeletor escolaAtual={escola.info} /> : undefined}
                utilizador={user ? { nome: user.nome, email: user.email, foto: user.foto_link } : null}
                menuUtilizador={[
                    { rotulo: "Minha conta", icone: <Icon icon="mdi:account-circle-outline" />, href: urls.conta, externo: true },
                    { rotulo: "Ajuda", icone: <Icon icon="mdi:help-circle-outline" />, href: urls.ajuda ? `${urls.ajuda}/p/escola-inteligente` : undefined, externo: true, oculto: !urls.ajuda },
                    { rotulo: "Novidades", icone: <Icon icon="mdi:creation-outline" />, onSelect: apresentacao.abrirNovidades },
                    { rotulo: "Ver o tutorial", icone: <Icon icon="mdi:map-marker-path" />, onSelect: apresentacao.verTutorial },
                ]}
                // O hash é base64: um «+» sem codificar chegava como espaço, a decifração falhava e a sessão ficava aberta.
                sair={urls.conta ? { href: `${urls.conta}/sair?ph=1&hash=${encodeURIComponent((window as any).hash ?? "")}` } : null}
                accoesCabecalho={
                    <>
                        <BotaoIcone rotulo="Pesquisar alunos" tamanho="lg" className="hidden sm:inline-flex" data-tour="pesquisar-alunos" onClick={() => setPesquisarAlunos(true)}>
                            <Icon icon="mdi:account-search-outline" className="size-5" />
                        </BotaoIcone>
                        {escola?.is_creche && (
                            <BotaoIcone rotulo="Enviar ocorrências" tamanho="lg" onClick={() => setShowDialogOcorrencias(true)}>
                                <Mailbox className="size-5" />
                            </BotaoIcone>
                        )}
                    </>
                }
                notificacoes={{ href: "/notificacoes", total: totalNotificacoes }}
                barraInferior={[
                    { chave: "inicio", rotulo: "Início", icone: "mdi:view-dashboard-outline", href: "/", activo: caminho === "/" },
                    { chave: "alunos", rotulo: "Alunos", icone: "mdi:account-school-outline", href: "/alunos", activo: caminho === "/alunos" || caminho.startsWith("/alunos/") },
                    { chave: "pesquisar", rotulo: "Pesquisar", icone: "mdi:magnify", abrePesquisa: true },
                    { chave: "avisos", rotulo: "Avisos", icone: "mdi:bell-outline", href: "/notificacoes", activo: caminho.startsWith("/notificacoes"), contagem: totalNotificacoes || null },
                ]}
                accoesPaleta={[
                    { chave: "pesquisar-alunos", rotulo: "Pesquisar alunos", descricao: "Pelo nome do aluno", icone: "mdi:account-search-outline", palavras: ["aluno", "estudante", "procurar"], onSelect: () => setPesquisarAlunos(true) },
                ]}
                tema={{ escolhido: tema, escuro, mudar: setTema }}
                semMargens={semMargens}
                // A rescisão do contrato e a eliminação agendada: avisos fixos até ao dia (só aqui, no painel de quem gere).
                avisos={
                    props.aviso_rescisao_escola || props.aviso_eliminacao_escola ? (
                        <>
                            {props.aviso_eliminacao_escola && <AvisoRescisao aviso={props.aviso_eliminacao_escola} />}
                            {props.aviso_rescisao_escola && <AvisoRescisao aviso={props.aviso_rescisao_escola} />}
                        </>
                    ) : undefined
                }
            >
                {children}
            </EstruturaPainel>

            <DialogPesquisarAlunos opened={pesquisarAlunos} onClose={() => setPesquisarAlunos(false)} />
            <JanelaNovidades aberta={apresentacao.novidadesAbertas} onFechar={apresentacao.fecharNovidades} produto="Escola Inteligente" artigo="a" diapositivos={NOVIDADES_ESCOLA} />
            <AvisoServicosPagos />
        </>
    );
}
